import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api, { resolveImage } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import CredibilitySection from "@/components/CredibilitySection";
import { useContent } from "@/context/ContentContext";
import { ArrowUpRight, Award, Factory, ShieldCheck, Truck, BookOpenCheck, PhoneCall } from "lucide-react";
import { Button } from "@/components/ui/button";

const CAT_IMG_FALLBACK = {
  "icu-beds": "https://images.unsplash.com/photo-1710074213379-2a9c2653046a",
  "hospital-beds": "https://images.unsplash.com/photo-1710074213379-2a9c2653046a",
  "stretchers": "https://images.pexels.com/photos/8943282/pexels-photo-8943282.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "wheelchairs": "https://images.pexels.com/photos/8543046/pexels-photo-8543046.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "iv-stands": "https://images.pexels.com/photos/4421486/pexels-photo-4421486.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "examination-tables": "https://images.pexels.com/photos/7789609/pexels-photo-7789609.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "medical-cabinets": "https://images.pexels.com/photos/9629685/pexels-photo-9629685.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "patient-chairs": "https://images.pexels.com/photos/8543046/pexels-photo-8543046.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "ot-tables": "https://images.pexels.com/photos/7789609/pexels-photo-7789609.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

export default function Home() {
  const { content } = useContent();
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/products?featured=true&limit=8").then((r) => setFeatured(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);

  const h = content?.hero || {};
  const bulk = content?.bulk_cta || {};

  return (
    <div>
      {/* HERO */}
      <section className="relative bg-white border-b border-slate-200 overflow-hidden">
        <div className="grid-bg absolute inset-0 opacity-40 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 py-16 md:py-24 grid lg:grid-cols-12 gap-8 relative">
          <div className="lg:col-span-7 flex flex-col justify-center">
            <div className="inline-flex items-center gap-2 border border-slate-300 bg-white px-3 py-1.5 mb-6 w-fit animate-tmf-fade">
              <div className="w-1.5 h-1.5 bg-tmf-blue animate-pulse" />
              <span className="text-[10px] font-mono-tmf uppercase tracking-widest text-tmf-navy">{h.eyebrow}</span>
            </div>

            <h1 data-testid="hero-heading" className="font-display font-black text-5xl md:text-7xl leading-[0.9] tracking-tighter text-tmf-navy uppercase animate-tmf-rise" style={{ animationDelay: "80ms" }}>
              {h.title_line1} <br />
              <span className="text-tmf-blue">{h.title_line2}</span>
            </h1>

            <p className="mt-6 text-base md:text-lg text-slate-600 max-w-xl leading-relaxed animate-tmf-rise" style={{ animationDelay: "180ms" }}>{h.subtitle}</p>

            <div className="mt-8 flex flex-wrap gap-3 animate-tmf-rise" style={{ animationDelay: "280ms" }}>
              <Button asChild data-testid="hero-shop-cta" className="rounded-none bg-tmf-blue hover:bg-tmf-navy text-white h-12 px-6 text-sm font-bold uppercase tracking-widest hover-lift">
                <Link to="/products">{h.cta_primary_label || "Browse Catalog"} <ArrowUpRight size={16} className="ml-2" /></Link>
              </Button>
              <Button asChild variant="outline" data-testid="hero-enquiry-cta" className="rounded-none border-tmf-navy text-tmf-navy hover:bg-tmf-navy hover:text-white h-12 px-6 text-sm font-bold uppercase tracking-widest hover-lift">
                <Link to="/contact">{h.cta_secondary_label || "Bulk Enquiry"}</Link>
              </Button>
            </div>

            <div className="mt-12 grid grid-cols-3 gap-6 max-w-xl">
              {(h.stats || []).map((s, i) => (
                <div key={s.l} className="border-l-2 border-tmf-blue pl-3 animate-tmf-rise" style={{ animationDelay: `${380 + i * 80}ms` }}>
                  <div className="font-display font-black text-3xl text-tmf-navy">{s.n}</div>
                  <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 grid grid-cols-2 gap-3 animate-tmf-fade" style={{ animationDelay: "150ms" }}>
            <div className="col-span-2 aspect-[16/10] overflow-hidden border border-slate-200">
              {h.image_1 && <img src={resolveImage(h.image_1)} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />}
            </div>
            <div className="aspect-square overflow-hidden border border-slate-200">
              {h.image_2 && <img src={resolveImage(h.image_2)} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />}
            </div>
            <div className="aspect-square bg-tmf-navy text-white p-5 flex flex-col justify-between">
              <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-tmf-sky">Warranty</div>
              <div>
                <div className="font-display font-black text-4xl leading-none">{h.warranty_label || "1-3 Yr"}</div>
                <div className="text-xs text-slate-300 mt-2">{h.warranty_note}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE TRUST */}
      <section className="bg-tmf-navy text-white py-4 overflow-hidden border-y border-slate-800">
        <div className="animate-marquee flex gap-12 whitespace-nowrap font-mono-tmf uppercase tracking-widest text-sm">
          {Array(2).fill(null).map((_, i) => (
            <div key={i} className="flex gap-12 shrink-0">
              <span className="flex items-center gap-2"><Factory size={16} className="text-tmf-sky" /> Made in India</span>
              <span className="flex items-center gap-2"><ShieldCheck size={16} className="text-tmf-sky" /> GeM Listed OEM</span>
              <span className="flex items-center gap-2"><Award size={16} className="text-tmf-sky" /> ISO 13485 · 9001 · CE</span>
              <span className="flex items-center gap-2"><Truck size={16} className="text-tmf-sky" /> Pan-India Delivery</span>
              <span className="flex items-center gap-2"><BookOpenCheck size={16} className="text-tmf-sky" /> Setup Guide + Video</span>
              <span className="flex items-center gap-2"><PhoneCall size={16} className="text-tmf-sky" /> 24×7 Support</span>
            </div>
          ))}
        </div>
      </section>

      {/* CREDIBILITY */}
      <CredibilitySection />

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 py-20 border-t border-slate-200">
        <div className="flex items-end justify-between mb-10 border-b border-slate-200 pb-6">
          <div>
            <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-tmf-blue mb-2">01 / Catalog</div>
            <h2 className="font-display font-black text-4xl md:text-5xl uppercase tracking-tight text-tmf-navy">Shop by Category</h2>
          </div>
          <Link to="/products" data-testid="view-all-categories" className="hidden md:flex items-center gap-1 font-mono-tmf text-xs uppercase tracking-widest hover:text-tmf-blue">
            View All <ArrowUpRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {categories.map((c, idx) => {
            const catImg = content?.category_images?.[c.slug] || CAT_IMG_FALLBACK[c.slug];
            return (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              data-testid={`category-${c.slug}`}
              style={{ animationDelay: `${idx * 60}ms` }}
              className="group relative bg-white border border-slate-200 aspect-[4/5] overflow-hidden brutal-shadow-hover animate-tmf-rise"
            >
              <img
                src={resolveImage(catImg)}
                alt={c.name}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              {/* Bottom-only white panel for text readability */}
              <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-white via-white/95 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-5">
                <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-tmf-blue mb-1 font-bold">Category</div>
                <div className="font-display font-black text-xl text-slate-900 uppercase leading-tight">{c.name}</div>
                <div className="text-xs text-slate-700 mt-1 font-medium">{c.description}</div>
                <ArrowUpRight size={20} className="text-white drop-shadow-md absolute top-4 right-4 group-hover:text-tmf-sky group-hover:rotate-12 transition-all duration-300" />
              </div>
            </Link>
          );})}
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-4 py-20 border-t border-slate-200">
        <div className="flex items-end justify-between mb-10 border-b border-slate-200 pb-6">
          <div>
            <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-tmf-blue mb-2">02 / Bestsellers</div>
            <h2 className="font-display font-black text-4xl md:text-5xl uppercase tracking-tight text-tmf-navy">Factory Favorites</h2>
          </div>
          <Link to="/products" className="hidden md:flex items-center gap-1 font-mono-tmf text-xs uppercase tracking-widest hover:text-tmf-blue">
            All Products <ArrowUpRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {featured.map((p) => <ProductCard key={p.product_id} product={p} />)}
        </div>
      </section>

      {/* WHY BUY DIRECT */}
      <section className="bg-tmf-navy text-white py-20 border-y border-slate-800">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-tmf-sky mb-2">03 / Advantage</div>
          <h2 className="font-display font-black text-4xl md:text-5xl uppercase tracking-tight mb-12 max-w-3xl">
            Why buy direct from the factory floor?
          </h2>

          <div className="grid md:grid-cols-3 gap-px bg-slate-800">
            {[
              { icon: Factory, title: "No Middlemen", desc: "You buy from us, the manufacturer. What retailers mark up 40%, we pass to you." },
              { icon: ShieldCheck, title: "GeM-Listed OEM", desc: "Government e-Marketplace verified original equipment manufacturer." },
              { icon: Award, title: "ISO 13485 · 9001 · CE", desc: "Medical devices QMS certified. Load-tested, hospital-grade." },
              { icon: BookOpenCheck, title: "Setup & Install Guide", desc: "Every unit ships pre-assembled where possible, with an illustrated PDF + step-by-step setup video. Live WhatsApp video-call support if you need a hand." },
              { icon: Truck, title: "Pan-India Logistics", desc: "In-house logistics for tier-1 to tier-4 cities. Global freight on request." },
              { icon: PhoneCall, title: "Real Humans", desc: "Talk to our engineers, not chatbots. Bulk quotations in under 24 hours." },
            ].map((f) => (
              <div key={f.title} className="bg-tmf-navy p-8 hover:bg-slate-950 transition-colors">
                <f.icon size={28} className="text-tmf-sky mb-4" />
                <div className="font-display font-bold text-lg uppercase tracking-tight mb-2">{f.title}</div>
                <div className="text-sm text-slate-300 leading-relaxed">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BULK CTA */}
      <section className="max-w-7xl mx-auto px-4 py-20">
        <div className="border border-tmf-navy p-8 md:p-16 grid md:grid-cols-2 gap-8 items-center relative overflow-hidden">
          <div className="absolute -top-4 -right-4 w-32 h-32 bg-tmf-blue -z-0" />
          <div className="relative z-10">
            <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-tmf-blue mb-2">{bulk.eyebrow}</div>
            <h2 className="font-display font-black text-3xl md:text-5xl uppercase tracking-tight text-tmf-navy leading-none">
              {bulk.title}
            </h2>
            <p className="mt-4 text-slate-600 max-w-md">{bulk.subtitle}</p>
          </div>
          <div className="relative z-10 flex flex-col gap-3 md:items-end">
            <Button asChild data-testid="cta-contact" className="rounded-none bg-tmf-navy hover:bg-tmf-blue text-white h-12 px-6 text-sm font-bold uppercase tracking-widest w-fit">
              <Link to="/contact">{bulk.cta_label || "Request Bulk Quote"}</Link>
            </Button>
            <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-slate-500">{bulk.note}</div>
          </div>
        </div>
      </section>
    </div>
  );
}
